import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useBlocker } from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';
import { Form, Formik, useFormikContext } from 'formik';
import moment from 'moment';

import ThreeLevelSteps from '../../../../common/steps';
import AddProductsDialog from '../../../../common/warehouse_and_food_partner_order_form/add_products_dialog';
import { getFoodPartnerAction } from '../../../../../../features/food_partner/get_food_partner';
import useNotification from '../../../../../../core/hooks/use_notification';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_ISO_FORMAT } from '../../../../../../core/constants';
import { getFoodPartnerNotificationData } from '../../../../../../core/utils/notification_utils';
import ProductsList from '../common/products_list';
import AdditionalOptions from '../common/additional_options';
import UploadFiles from '../../../../common/warehouse_and_food_partner_order_form/upload_files';
import TextFieldFormik from '../../../../../common/text_field';
import useUnsavedChangesWarning from '../../../../../layout/component/use_unsaved_changes_warning';
import BottomActionBar from '../../../../../common/bottom_action_bar';
import { SubmitButton } from '../../../../../common/button';
import SectionPaper from '../../../../../common/section_paper';
import BlockerDialog from '../../../../common/blocker_dialog';
import BlockerReturnButton from '../../../../common/blocker_return_button';
import { useDirtyForm } from '../../../../../../core/context/dirty_form_context';

const FormBody = ({ form, onChangeSetDate, onCopyHistoricalOrder, onReturn }) => {
    const formik = useFormikContext();
    useUnsavedChangesWarning(formik.dirty);
    const { setIsDirty } = useDirtyForm();

    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        formik.dirty && currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        setIsDirty(formik.dirty);

        return () => setIsDirty(false);
    }, [formik.dirty, setIsDirty]);

    return (
        <>
            <BlockerDialog blocker={ blocker }/>
            <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                <Box sx={ { flexGrow: 1, pb: 4 } }>
                    <Stack spacing={ 3 }>
                        <ThreeLevelSteps secondStep/>

                        <AdditionalOptions
                            onChangeSetDate={ (newDate, allDates) => onChangeSetDate(newDate, allDates, formik) }
                            displayCopyHistoricalDialog
                            form={ form }
                            onCopyHistoricalOrder={ (values) => onCopyHistoricalOrder(values, formik) }
                            formik={ formik }
                        />

                        <SectionPaper title="Lista produktów">
                            <Stack spacing={ 2 }>
                                <ProductsList
                                    formik={ formik }
                                    withExpirationDate
                                    productsListIndex={ 0 }
                                />
                                <AddProductsDialog
                                    foodPartnerId={ form?.foodPartnerId }
                                    formik={ formik }
                                    dateToCalculateExpirationDate={ form?.orderDate }
                                    productsListIndex={ 0 }
                                />
                            </Stack>
                        </SectionPaper>

                        <SectionPaper>
                            <Stack spacing={ 3 }>
                                <UploadFiles formik={ formik } addAndRemoveOptions/>
                                <Box>
                                    <Typography variant="subtitle2" sx={ {
                                        fontWeight: 600,
                                        mb: 1,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase'
                                    } }>
                                        Notatka do zamówienia
                                    </Typography>
                                    <TextFieldFormik name="orderNote" multiline rows={ 3 }/>
                                </Box>
                            </Stack>
                        </SectionPaper>
                    </Stack>
                </Box>

                <BottomActionBar>
                    <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                        <BlockerReturnButton formik={ formik } onReturn={ () => onReturn() }/>
                        <SubmitButton type="button" onClick={ formik.submitForm }>Dalej</SubmitButton>
                    </Box>
                </BottomActionBar>
            </Box>
        </>
    );
};

const FormWithoutLocationDivision = ({ form, setView, data, setData }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    useEffect(() => {
        dispatch(getFoodPartnerAction(form?.foodPartnerId))
            .then(response => showErrorMessage(getFoodPartnerNotificationData(response)));
    }, [dispatch, form, showErrorMessage]);

    const initialValues = {
        productsList: data?.formik?.productsList || [],
        attachments: data?.formik?.attachments || [],
        orderNote: data?.formik?.orderNote || '',
    };

    const onReturn = () => {
        setData({});
        setView(0);
    };

    const onCopyHistoricalOrder = (values, formik) => {
        const productsList = [];

        values?.forEach((product, index) => {
            productsList.push({
                position: index,
                name: product?.productName,
                productId: product?.productId,
                productBatchId: '',
                quantity: product?.quantity,
                expirationDate: product?.defaultDaysToExpiration ? moment(form?.orderDate).add(Math.max(product?.defaultDaysToExpiration - 1, 0), 'days') : '',
                price: product?.purchaseNetPrice ? Number(product.purchaseNetPrice).toFixed(2) : '',
                type: product?.type
            });
        });

        formik.setFieldValue(`productsList.${ 0 }`, productsList);
    };

    const handleSubmit = (values) => {
        const locationProducts = [{
            products: [
                ...values?.productsList?.[0]?.map(item => ({
                    productId: item?.productId,
                    quantity: item?.quantity,
                    price: item?.price,
                    expirationDate: item?.expirationDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                }))
            ]
        }];

        setData({
            form: {
                ...form,
                orderDate: form?.orderDate?.format(MISC_DATE_ISO_FORMAT),
                locationProducts,
                attachments: values?.attachments || [],
                orderNote: values?.orderNote
            },
            formik: values
        });
        setView(3);
    };

    const validate = (values) => {
        const errors = {};
        const productsListErrors = [];
        let hasErrors = false;

        if (!values.productsList || values.productsList.length === 0) {
            errors.productsList = ['Wybierz minimum 1 produkt'];
            return errors;
        }

        values.productsList.forEach((products, index) => {
            if (!products || products.length === 0) {
                productsListErrors[index] = 'Wybierz minimum 1 produkt';
                hasErrors = true;
            } else {
                const itemErrorsArray = [];
                products.forEach((item, itemIndex) => {
                    const itemErrors = {};

                    if (!item?.quantity) {
                        itemErrors.quantity = 'Pole wymagane';
                    } else if (item?.quantity <= 0) {
                        itemErrors.quantity = 'Proszę podać wartość całkowitą dodatnią';
                    }

                    if (item?.price && item?.price <= 0) {
                        itemErrors.price = 'Proszę podać wartość dodatnią';
                    }

                    if (!item?.expirationDate) {
                        itemErrors.expirationDate = 'Pole wymagane';
                    }

                    if (Object.keys(itemErrors).length > 0) {
                        itemErrorsArray[itemIndex] = itemErrors;
                        hasErrors = true;
                    }
                });

                if (itemErrorsArray.length > 0 || productsListErrors[index]) {
                    productsListErrors[index] = productsListErrors[index] || itemErrorsArray;
                }
            }
        });

        if (hasErrors) {
            errors.productsList = productsListErrors;
        }

        return errors;
    };

    const onChangeSetDate = (newDate, allDates, formik) => {
        const productsList = formik?.values?.productsList[0]?.map(item => {
            if (!allDates && item?.expirationDate) {
                return item;
            }

            return ({ ...item, expirationDate: newDate });
        });

        formik.setFieldValue(`productsList.${ 0 }`, productsList);
    };

    return (
        <Formik
            initialValues={ initialValues }
            validate={ validate }
            onSubmit={ handleSubmit }
        >
            <FormBody
                form={ form }
                onChangeSetDate={ onChangeSetDate }
                onCopyHistoricalOrder={ onCopyHistoricalOrder }
                onReturn={ onReturn }
            />
        </Formik>
    );
};

export default FormWithoutLocationDivision;